import { useEffect, useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { cleanReports, fetchUnmergeLayouts, UnmergeLayout, UnmergeSummary } from '../../api/batch4Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload one or more Sage report workbooks (.xlsx).',
  'The layout of each sheet is auto-detected (by title, then by content). Force a layout only if detection fails.',
  'Click Unmerge & Realign. A cleaned workbook (or a ZIP for several files) downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

const selectClass = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground';

function UnmergePanel({ mission, onBack }: PanelProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [layouts, setLayouts] = useState<UnmergeLayout[]>([]);
  const [forced, setForced] = useState('');
  const { status, error, result, fail, run } = useFileRun<FileResult<UnmergeSummary>>();

  useEffect(() => {
    fetchUnmergeLayouts().then(setLayouts).catch(() => setLayouts([]));
  }, []);

  const handleSubmit = async () => {
    if (files.length === 0) {
      fail('Upload at least one .xlsx file.');
      return;
    }
    await run(() => cleanReports(files, forced || null), 'Cleaning failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      {layouts.length > 0 && (
        <details className="mb-5 rounded-lg border border-border bg-muted px-4 py-3">
          <summary className="cursor-pointer text-sm font-semibold text-foreground">How layout detection works</summary>
          <p className="mt-2 text-sm text-muted-foreground">
            Same rows, same headers, same formatting: only mis-placed amount cells move. Supported layouts:
          </p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {layouts.map((l) => (
              <li key={l.key}>{l.label}</li>
            ))}
          </ul>
        </details>
      )}

      <FileDrop
        label="Sage report(s)"
        required
        multiple
        accept={mission.accept}
        hint={mission.maxSizeLabel}
        accentColor={mission.color}
        files={files}
        onFiles={setFiles}
      />

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="um-layout" className="mb-1.5">
          Force a layout (optional)
        </Label>
        <select id="um-layout" className={selectClass} value={forced} onChange={(e) => setForced(e.target.value)}>
          <option value="">Auto-detect</option>
          {layouts.map((l) => (
            <option key={l.key} value={l.key}>
              {l.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-muted-foreground">
          Only needed when a sheet's name or content doesn't auto-detect correctly.
        </p>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Cleaning…' : 'Unmerge & Realign'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Cleaning…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && s && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">
            ✅ {s.cleaned}/{s.total} file(s) cleaned. {result.filename} downloaded.
          </div>
          {s.files.map((f) => (
            <div
              key={f.file}
              className={`rounded-lg px-4 py-3 ${
                f.status === 'ok' ? 'bg-muted text-foreground' : f.status === 'skip' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'
              }`}
            >
              <strong>{f.file}</strong>
              {f.status === 'ok' ? (
                <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground">
                  {f.sheets?.map((sh) => (
                    <li key={sh.sheet}>
                      {sh.sheet} [{sh.layout_label}]: unmerged {sh.unmerged}, moved {sh.moved}
                      {sh.collisions > 0 && <strong className="text-red-700">, {sh.collisions} collision(s)!</strong>}
                    </li>
                  ))}
                </ul>
              ) : (
                <span>: {f.message}</span>
              )}
            </div>
          ))}
        </div>
      )}
      {status === 'idle' && <InfoNotice>📁 Upload one or more .xlsx files to get started.</InfoNotice>}
    </MissionShell>
  );
}

export default UnmergePanel;
